import { OrderTypeEnum } from '@core/enums';
import { MenuItem, MenuItemForm } from '@core/models';
import { HomeFactory } from './home-factory.service';
import {
	HomeMenuItemModalRule,
	HomeMenuItemProcessingContext,
	HomeMenuItemViewState,
	HomeProcessingContext,
	HomeProcessingState,
	HomeSingleProductOrderContext,
	HomeSingleProductOrderState,
	HomeSingleProductFormContext,
	HomeSingleProductFormState,
	HomeSingleProductSubmissionContext,
	HomeSingleProductSubmissionState,
	HomeSingleProductDisplayContext,
	HomeSingleProductDisplayState,
	HomeSuggestedProductAvailabilityContext,
	HomeSuggestedProductAvailabilityState,
} from './interfaces';

export class HomeProcessor {
	constructor(private readonly factory: HomeFactory) {}

	process(context: HomeProcessingContext): HomeProcessingState {
		const initialState: HomeProcessingState = {
			...context,
			isLoggedIn: false,
			showEmptyProducts: false,
			icon: 'user',
			hasValidDeliveryZone: false,
			isDeliveryMode: false,
			nextOrderType: OrderTypeEnum.Delivery,
			address: null,
		};

		return this.factory
			.createRules()
			.reduce((currentState, rule) => rule(currentState), initialState);
	}

	processMenuItem(
		context: Omit<HomeMenuItemProcessingContext, 'viewState'>
	): HomeMenuItemViewState {
		const initialContext: HomeMenuItemProcessingContext = {
			...context,
			viewState: {
				product: null,
				nombre: undefined,
				imagen: null,
				descripcion: undefined,
				price: 0n,
				specialPriceAmount: 0n,
				specialPriceValue: null,
				specialPriceIsPercent: true,
				showPromotion: false,
				cantItemsInCart: 0,
				totalQtyItemsInCart: context.totalQuantityInCart,
				productInOrder: false,
				cantidadProducto: 1n,
				stock: null,
				isOutOfStock: false,
			},
		};

		return this.factory
			.createMenuItemRules()
			.reduce((state, rule) => rule(state), initialContext).viewState;
	}

	getMenuItemModalCssClass(item: MenuItem | null): ReturnType<HomeMenuItemModalRule> {
		return this.factory.createMenuItemModalRule()(item);
	}

	processSingleProductOrder(context: HomeSingleProductOrderContext): MenuItem {
		const { productosCanal: _formChannel, ...formValues } = context.formValues ?? {};
		const initialContext: HomeSingleProductOrderState = {
			...context,
			article: null,
			orderItem:
				context.formData ??
				({
					...formValues,
					productosCanal: [],
					detailsWithQuantity: context.detailsWithQuantity ?? [],
					detailsWithQuantityNoAdd: context.detailsWithQuantityNoAdd ?? [],
				} as MenuItem),
			channel: context.formValues?.productosCanal ?? context.formData?.productosCanal?.[0],
			isSimpleProduct: false,
		};

		return this.factory
			.createSingleProductOrderRules()
			.reduce((currentContext, rule) => rule(currentContext), initialContext).orderItem;
	}

	processSingleProductForm(context: HomeSingleProductFormContext): MenuItemForm {
		const initialContext: HomeSingleProductFormState = { ...context };
		const result = this.factory
			.createSingleProductFormRules()
			.reduce((currentContext, rule) => rule(currentContext), initialContext);

		if (!result.formValue) {
			throw new Error('Single-product form rules did not produce a form value.');
		}

		return result.formValue;
	}

	isSingleProductSubmissionDisabled(context: HomeSingleProductSubmissionContext): boolean {
		const initialState: HomeSingleProductSubmissionState = {
			...context,
			isQuantityValid: false,
			activeModifierCounts: [],
			hasUnmetModifierMinimum: false,
			isDisabled: false,
		};

		return this.factory
			.createSingleProductSubmissionRules()
			.reduce((state, rule) => rule(state), initialState).isDisabled;
	}

	isSingleProductLongMode(context: HomeSingleProductDisplayContext): boolean {
		const initialState: HomeSingleProductDisplayState = {
			...context,
			hasModifierGroups: false,
			hasMultiplePrices: false,
			isLongMode: false,
		};

		return this.factory
			.createSingleProductDisplayRules()
			.reduce((state, rule) => rule(state), initialState).isLongMode;
	}

	getSuggestedProductAvailability(
		context: HomeSuggestedProductAvailabilityContext
	): HomeSuggestedProductAvailabilityState {
		const initialState: HomeSuggestedProductAvailabilityState = {
			...context,
			isAvailable: false,
			isOutOfStock: false,
		};

		return this.factory
			.createSuggestedProductAvailabilityRules()
			.reduce((state, rule) => rule(state), initialState);
	}
}

export const homeProcessor = new HomeProcessor(new HomeFactory());